import type { TenantCompanyProfile } from '@forq/platform-data';

/**
 * Kundenblatt (Stammblatt) als druckfertiges HTML im FTW-Layout -- die vollständige Kundenakte auf
 * einem Blatt: Stammdaten, Kontakt, Anschriften, Ansprechpartner, Konditionen & Buchhaltung und die
 * Kennzahlen (Umsatz, offene Posten, Maschinen, Vorgänge). Gleicher Briefkopf/Fußzeile wie die Miet-
 * und Rechnungsbelege (aus dem Firmenprofil). Gedruckt/als PDF gespeichert wird über den Browser.
 */

export interface CustomerSheetAddress {
  readonly label: string;
  readonly kindLabel: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly country: string;
  readonly primary: boolean;
}
export interface CustomerSheetContact {
  readonly name: string;
  readonly position: string;
  readonly email: string;
  readonly phone: string;
  readonly primary: boolean;
}
export interface CustomerSheetMachine {
  readonly number: string;
  readonly label: string;
  readonly statusLabel: string;
}
export interface CustomerSheetContext {
  readonly company: TenantCompanyProfile;
  readonly todayIso: string;
  readonly customer: {
    readonly number: string;
    readonly displayName: string;
    readonly kindLabel: string;
    readonly statusLabel: string;
    readonly vatId: string;
    readonly taxNumber: string;
    readonly website: string;
    readonly phone: string;
    readonly email: string;
    readonly activeSince: string;
    readonly notes: string;
  };
  readonly agreements: {
    readonly debtorNumber: string;
    /** Leitweg-ID (BT-10) -- nur bei oeffentlichen Auftraggebern belegt. */
    readonly leitwegId: string;
    readonly paymentTermDays: number;
    readonly discountPercent: number;
    readonly creditLimit: number;
    readonly bankName: string;
    readonly iban: string;
    readonly bic: string;
    readonly conditions: string;
  };
  readonly addresses: readonly CustomerSheetAddress[];
  readonly contacts: readonly CustomerSheetContact[];
  readonly machines: readonly CustomerSheetMachine[];
  readonly kpis: {
    readonly revenue: number;
    readonly openReceivables: number;
    readonly machineCount: number;
    readonly operationCount: number;
  };
}

// --- Formatierung -----------------------------------------------------------

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function money(value: number): string { return euro.format(value); }
function dateLabel(value: string): string {
  const [year, month, day] = (value || '').split('-');
  return year && month && day ? `${day}.${month}.${year}` : (value || '–');
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

// --- Briefkopf / Fußzeile (aus dem Firmenprofil) ---------------------------

function letterhead(company: TenantCompanyProfile): string {
  const identity = company.identity;
  const lh = company.letterhead;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `
    <div class="hd">
      <div class="hd-slot hd-left">${slotLeft}</div>
      <div class="hd-slot hd-center">${slotCenter}</div>
      <div class="hd-slot hd-right">${slotRight}</div>
    </div>
    <hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile): string {
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col('Kontakt', [contact.phone && `Tel.: ${contact.phone}`, contact.fax && `Fax: ${contact.fax}`, contact.email, contact.website])}
    ${col('Bankverbindung', [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col('Unternehmen', [identity.managingDirectors && `Geschäftsführer: ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `USt-IdNr.: ${identity.vatId}`])}
  </div>`;
}

// --- Belegkörper ------------------------------------------------------------

function row(label: string, value: string): string {
  return `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>`;
}

function body(ctx: CustomerSheetContext): string {
  const { customer, agreements, addresses, contacts, machines, kpis } = ctx;
  const stammHtml = `<h2 class="sec">Stammdaten</h2>
    <table class="box">
      ${row('Kundennummer', customer.number)}
      ${row('Name', customer.displayName)}
      ${row('Art', customer.kindLabel)}
      ${row('Status', customer.statusLabel)}
      ${row('Kunde seit', dateLabel(customer.activeSince))}
      ${customer.vatId ? row('USt-IdNr.', customer.vatId) : ''}
      ${customer.taxNumber ? row('Steuernummer', customer.taxNumber) : ''}
      ${row('Telefon', customer.phone || '–')}
      ${row('E-Mail', customer.email || '–')}
      ${customer.website ? row('Web', customer.website) : ''}
    </table>`;

  const kpiHtml = `<h2 class="sec">Kennzahlen</h2>
    <div class="kpis">
      <div><span>Umsatz (gebucht)</span><strong>${money(kpis.revenue)}</strong></div>
      <div><span>Offene Posten</span><strong class="${kpis.openReceivables > 0 ? 'is-open' : ''}">${money(kpis.openReceivables)}</strong></div>
      <div><span>Maschinen</span><strong>${kpis.machineCount}</strong></div>
      <div><span>Vorgänge</span><strong>${kpis.operationCount}</strong></div>
    </div>`;

  const kondHtml = `<h2 class="sec">Konditionen &amp; Buchhaltung</h2>
    <table class="box">
      ${row('Debitorennummer', agreements.debtorNumber || '–')}
      ${agreements.leitwegId ? row('Leitweg-ID', agreements.leitwegId) : ''}
      ${row('Zahlungsziel', `${agreements.paymentTermDays} Tage`)}
      ${row('Rabatt', agreements.discountPercent > 0 ? `${agreements.discountPercent} %` : '–')}
      ${row('Kreditlimit', agreements.creditLimit > 0 ? money(agreements.creditLimit) : '–')}
      ${row('Bankverbindung', [agreements.bankName, agreements.iban, agreements.bic].filter(Boolean).join(' · ') || '–')}
      ${agreements.conditions ? row('Besondere Konditionen', agreements.conditions) : ''}
    </table>`;

  const addressHtml = addresses.length > 0 ? `<h2 class="sec">Anschriften</h2>
    <table class="grid">
      <tr><th>Art</th><th>Anschrift</th><th>Ort</th></tr>
      ${addresses.map((a) => `<tr><td>${esc(a.kindLabel)}${a.primary ? ' <em>(Haupt)</em>' : ''}</td><td>${esc(a.street || '–')}</td><td>${esc(`${a.postalCode} ${a.city}`.trim() || '–')}${a.country ? ` · ${esc(a.country)}` : ''}</td></tr>`).join('')}
    </table>` : '';

  const contactHtml = contacts.length > 0 ? `<h2 class="sec">Ansprechpartner</h2>
    <table class="grid">
      <tr><th>Name</th><th>Funktion</th><th>Kontakt</th></tr>
      ${contacts.map((c) => `<tr><td>${esc(c.name)}${c.primary ? ' <em>(Haupt)</em>' : ''}</td><td>${esc(c.position || '–')}</td><td>${esc([c.email, c.phone].filter(Boolean).join(' · ') || '–')}</td></tr>`).join('')}
    </table>` : '';

  const machineHtml = machines.length > 0 ? `<h2 class="sec">Zugeordnete Maschinen</h2>
    <table class="grid">
      <tr><th>Nummer</th><th>Maschine</th><th>Status</th></tr>
      ${machines.map((m) => `<tr><td>${esc(m.number)}</td><td>${esc(m.label)}</td><td>${esc(m.statusLabel)}</td></tr>`).join('')}
    </table>` : '';

  const notesHtml = customer.notes ? `<h2 class="sec">Bemerkungen</h2><p class="note">${esc(customer.notes)}</p>` : '';

  return `<div class="cols">
      <div class="col">${stammHtml}${kpiHtml}</div>
      <div class="col">${kondHtml}</div>
    </div>
    ${addressHtml}
    ${contactHtml}
    ${machineHtml}
    ${notesHtml}`;
}

function shell(ctx: CustomerSheetContext): string {
  const { company, customer, todayIso } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  const meta = `
    <div class="doc-head">
      <div class="doc-title">KUNDENBLATT</div>
      <table class="meta">
        <tr><th>Kunde:</th><td>${esc(customer.number)}</td></tr>
        <tr><th>Datum:</th><td>${dateLabel(todayIso)}</td></tr>
        <tr><th>Status:</th><td>${esc(customer.statusLabel)}</td></tr>
      </table>
    </div>`;
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>Kundenblatt ${esc(customer.number)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); position: relative; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 10mm; }
  .sender { font-size: 12px; color: #444; font-weight: 700; }
  .doc-head { min-width: 70mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 17px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  h1.name { font-size: 22px; font-weight: 800; color: var(--accent); margin: 8mm 0 0; }
  .band-rule { border: 0; border-top: 1px solid #bbb; margin: 6px 0 0; }
  h2.sec { font-size: 13.5px; font-weight: 700; margin: 6mm 0 2.5mm; color: #111; }
  .cols { display: flex; gap: 8mm; align-items: flex-start; }
  .cols .col { flex: 1; min-width: 0; }
  table.box { width: 100%; border-collapse: collapse; border: 1px solid #ccc; }
  table.box th { text-align: left; color: var(--label); font-weight: 700; padding: 5px 8px; width: 42%; vertical-align: top; border: 1px solid #e2e2e2; background: #fafafa; }
  table.box td { padding: 5px 8px; vertical-align: top; border: 1px solid #e2e2e2; }
  table.grid { width: 100%; border-collapse: collapse; border: 1px solid #ccc; }
  table.grid th { text-align: left; color: var(--label); font-weight: 700; padding: 4px 8px; border: 1px solid #e2e2e2; background: #fafafa; }
  table.grid td { padding: 4px 8px; border: 1px solid #e2e2e2; vertical-align: top; }
  table.grid em { color: var(--accent); font-style: normal; font-size: 9px; }
  .kpis { display: flex; gap: 4mm; }
  .kpis div { flex: 1; border: 1px solid #e2e2e2; border-radius: 4px; padding: 6px 8px; background: #fafafa; }
  .kpis span { display: block; color: #888; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
  .kpis strong { font-size: 15px; }
  .kpis strong.is-open { color: var(--accent); }
  .note { font-size: 10px; color: #444; margin: 3mm 0; text-align: justify; white-space: pre-line; }
  .footer { margin-top: 12mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    h2.sec { break-after: avoid; break-inside: avoid; }
    table.box, table.grid, .kpis { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band"><div class="sender">${esc(company.identity.name || '')}</div>${meta}</div>
    <hr class="band-rule">
    <h1 class="name">${esc(customer.displayName)}</h1>
    ${body(ctx)}
    ${footer(company)}
  </div>
</body></html>`;
}

/** Das vollständige HTML des Kundenblatts -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderCustomerSheetHtml(ctx: CustomerSheetContext): string {
  return shell(ctx);
}
